import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { GlobalContext } from './Context';
import { OpenMenu } from './Context/GlobalContext';
import Header from './Header';
import LoadingSkeleton from './LoadingSkeleton';
import Main from './Main';
import MainBody from './Main/MainBody';
import Navigation from './Navigation';
import NotFound from './NotFound';
import ThemeLoader from './ThemeLoader';
import Translations from './Translations';
import Deals from './Types/Deals';
import Invoices from './Types/Invoices/Invoices';
import Projects from './Types/Projects/Projects';
import Api from '../../Components/Context/Api';
import { useIsSmall } from '../../Components/Context/Hooks/mediaQueries';
import { BannerProps } from '../../Components/UI/Banner/Banner';
import Box from '../../Components/UI/Box';
import { isMenu, MENUS } from '../../Utils/Menus';
import getApi from '../../Utils/api';
import { DecodedUrlData, TYPES } from '../../Utils/jwt';

interface HomeData {
  jwtData: DecodedUrlData;
}

export enum SEARCH_PARAMS {
  OPEN_MENU = 'openMenu',
}

const Home: React.FC<HomeData> = ({ jwtData }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const menu = searchParams.get(SEARCH_PARAMS.OPEN_MENU);

  const { token, detailId, id } = useParams<HomeRouteProps>();
  const [openMenu, setOpenMenu] = useState<OpenMenu | null>(isMenu(menu) ? { menu, time: new Date() } : null);
  const [banners, setBanners] = useState<BannerProps[]>([]);
  const bannersRef = useRef<BannerProps[]>([]);
  const request = useMemo(() => getApi(token || ''), [token]);
  const isSmall = useIsSmall();

  const handleOpenMenu = useCallback(
    (menu: MENUS) => {
      setOpenMenu({ menu, time: new Date() });

      if (searchParams.has(SEARCH_PARAMS.OPEN_MENU)) {
        searchParams.delete(SEARCH_PARAMS.OPEN_MENU);
        setSearchParams(searchParams);
      }

      window.dispatchEvent(new Event('resize'));
    },
    [searchParams, setSearchParams],
  );

  const handleCloseMenu = useCallback(() => {
    setOpenMenu(null);

    if (searchParams.has(SEARCH_PARAMS.OPEN_MENU)) {
      searchParams.delete(SEARCH_PARAMS.OPEN_MENU);
      setSearchParams(searchParams);
    }

    window.dispatchEvent(new Event('resize'));
  }, [searchParams, setSearchParams]);

  const defaultLanguage = navigator.language.split('-')[0];

  const handleAddBanner = useCallback((bannerProps: BannerProps, dismissable: boolean) => {
    const closeBanner = () => {
      bannersRef.current.splice(bannersRef.current.indexOf(bannerProps), 1);
      setBanners([...bannersRef.current]);
    };

    bannersRef.current.push({
      ...bannerProps,
      dismissFunction: dismissable ? closeBanner : bannerProps.dismissFunction,
    });
    setBanners([...bannersRef.current]);

    return closeBanner;
  }, []);

  return (
    <Api token={token}>
      <ThemeLoader>
        {({ loading, data: theme, accountSettings }) => {
          let component: React.ReactNode;

          switch (jwtData.type) {
            case TYPES.DEAL:
              component = <Deals quotationId={detailId} themeLoading={loading} />;
              break;

            case TYPES.INVOICE:
              component = <Invoices invoiceId={id || jwtData.invoiceUuid!} themeLoading={loading} />;
              break;

            case TYPES.PROJECT:
              component = <Projects projectId={id || jwtData.projectId!} themeLoading={loading} />;
              break;

            default:
              component = (
                <Translations
                  loader={<LoadingSkeleton style={isSmall ? 'mobile' : 'collapsed'} />}
                  loading={loading}
                  language={defaultLanguage}
                >
                  <Main>
                    <Navigation />
                    <Box flex={1} display="flex" flexDirection="column" overflow="auto">
                      <Header />
                      <MainBody language={defaultLanguage}>
                        <NotFound />
                      </MainBody>
                    </Box>
                  </Main>
                </Translations>
              );
              break;
          }
          const isAccountSettingEnabled = (name: string) =>
            !!accountSettings?.find((accountSetting) => accountSetting.name === name)?.enabled;
          return (
            <GlobalContext.Provider
              value={{
                request,
                paymentServiceProvidersEnabled: theme?.paymentServiceProvidersEnabled,
                logo: theme?.logo,
                handleOpenMenu,
                handleCloseMenu,
                openMenu,
                type: jwtData.type,
                handleAddBanner,
                banners,
                accountSettings,
                isAccountSettingEnabled,
              }}
            >
              {component}
            </GlobalContext.Provider>
          );
        }}
      </ThemeLoader>
    </Api>
  );
};

export default Home;
